import { supabase } from "@/integrations/supabase/client";
import type { Tables } from "@/integrations/supabase/types";
import { getCurrentUser as getAuthUser } from "@/services/data/auth";

export type User = Tables<"users">;

export const getCurrentUser = async () => getAuthUser();

export const getUserProfile = async (userId: string) => {
  try {
    const { data, error } = await supabase
      .from("users")
      .select("first_name, last_name, avatar_url, phone, job_title")
      .eq("id", userId)
      .maybeSingle();

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    return { success: false, error };
  }
};

export const getUserById = async (userId: string) => {
  try {
    const { data, error } = await supabase
      .from("users")
      .select("*")
      .eq("id", userId)
      .maybeSingle();

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error("Error fetching user:", error);
    return { success: false, error };
  }
};

export const getUserByEmail = async (email: string) => {
  try {
    const { data, error } = await supabase
      .from("users")
      .select("*")
      .eq("email", email)
      .maybeSingle();

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error("Error fetching user by email:", error);
    return { success: false, error };
  }
};

export interface ActiveUser {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  avatar_url: string | null;
  is_active: boolean | null;
}

/**
 * Get all active users
 */
export const getAllActiveUsers = async (): Promise<{
  success: boolean;
  data?: ActiveUser[];
  error?: string;
}> => {
  try {
    const { data, error } = await supabase
      .from('users')
      .select('id, email, first_name, last_name, avatar_url, is_active')
      .eq('is_active', true)
      .order('first_name', { ascending: true });

    if (error) {
      return { success: false, error: error.message };
    }

    return { success: true, data: data || [] };
  } catch (error) {
    console.error('Error fetching active users:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
};

export interface TeamMember {
  id: string;
  first_name: string | null;
  last_name: string | null;
  avatar_url: string | null;
  role: 'pbm' | 'ops';
}

type UserBasicData = {
  id: string;
  first_name: string | null;
  last_name: string | null;
  avatar_url: string | null;
};

/**
 * Fetch a user's basic data by ID
 */
const fetchUserBasicData = async (userId: string): Promise<UserBasicData | null> => {
  const { data, error } = await supabase
    .from('users')
    .select('id, first_name, last_name, avatar_url')
    .eq('id', userId)
    .maybeSingle();

  if (error) {
    console.error('Error fetching user:', error);
    return null;
  }
  return data;
};

/**
 * Fetch the PBM team member for a customer
 */
const fetchPbmTeamMember = async (pbmId: string): Promise<TeamMember | null> => {
  const { data: pbmRecord, error: pbmError } = await supabase
    .from('team_personal_brand_managers')
    .select('user_id')
    .eq('id', pbmId)
    .maybeSingle();

  if (pbmError) {
    console.error('Error fetching PBM record:', pbmError);
    return null;
  }

  if (!pbmRecord?.user_id) {
    return null;
  }

  const userData = await fetchUserBasicData(pbmRecord.user_id);
  return userData ? { ...userData, role: 'pbm' } : null;
};

/**
 * Fetch the Ops team member for a customer
 */
const fetchOpsTeamMember = async (opsId: string): Promise<TeamMember | null> => {
  const userData = await fetchUserBasicData(opsId);
  return userData ? { ...userData, role: 'ops' } : null;
};

/**
 * Get the assigned team members (PBM, Ops) for a customer
 */
export const getAssignedTeam = async (customerId: string): Promise<{
  success: boolean;
  data?: TeamMember[];
  error?: string;
}> => {
  try {
    const { data: customer, error: customerError } = await supabase
      .from('users')
      .select('personal_brand_manager_id, ops_id')
      .eq('id', customerId)
      .maybeSingle();

    if (customerError) {
      console.error('Error fetching customer:', customerError);
      return { success: false, error: customerError.message };
    }

    if (!customer) {
      return { success: true, data: [] };
    }

    const [pbmMember, opsMember] = await Promise.all([
      customer.personal_brand_manager_id
        ? fetchPbmTeamMember(customer.personal_brand_manager_id)
        : null,
      customer.ops_id
        ? fetchOpsTeamMember(customer.ops_id)
        : null,
    ]);

    const teamMembers = [pbmMember, opsMember].filter(
      (member): member is TeamMember => member !== null
    );

    return { success: true, data: teamMembers };
  } catch (error) {
    console.error('Error fetching assigned team:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
};

export const updateUser = async (userId: string, updates: Partial<User>) => {
  try {
    const { data, error } = await supabase
      .from("users")
      .update(updates)
      .eq("id", userId)
      .select()
      .single();

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error("Error updating user:", error);
    return { success: false, error };
  }
};

export const updateUserProfile = async (userId: string, profile: { first_name: string; last_name: string; phone?: string | null; job_title?: string | null }) => {
  try {
    const { data, error } = await supabase
      .from("users")
      .update({
        first_name: profile.first_name,
        last_name: profile.last_name,
        phone: profile.phone,
        job_title: profile.job_title,
      })
      .eq("id", userId)
      .select()
      .single();

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    return { success: false, error };
  }
};

export const updateAvatarUrl = async (userId: string, avatarUrl: string) => {
  try {
    const { data, error } = await supabase
      .from("users")
      .update({ avatar_url: avatarUrl })
      .eq("id", userId);

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error("Error updating avatar URL:", error);
    return { success: false, error };
  }
};

export const getUserHasSignedUp = async (userId: string) => {
  try {
    const { data, error } = await supabase
      .from("users")
      .select("has_signed_up")
      .eq("id", userId)
      .single();

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error("Error fetching has_signed_up:", error);
    return { success: false, error };
  }
};

export const updateHasSignedUp = async (userId: string, hasSignedUp: boolean) => {
  try {
    const { data, error } = await supabase
      .from("users")
      .update({ has_signed_up: hasSignedUp })
      .eq("id", userId);

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error("Error updating has_signed_up:", error);
    return { success: false, error };
  }
};

export const deleteUserAccount = async () => {
  try {
    const { data, error } = await supabase.functions.invoke('delete-user');

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error("Error deleting user account:", error);
    return { success: false, error };
  }
};

/**
 * Admin function to delete a user or their content by email
 */
export const adminDeleteUser = async (
  email: string,
  contentOnly: boolean = false
): Promise<{
  success: boolean;
  data?: {
    deleted_from?: string[];
    error?: string;
  };
  error?: string;
}> => {
  try {
    const { data, error } = await supabase.functions.invoke('admin-delete-user', {
      body: { email, content_only: contentOnly },
    });

    if (error) {
      return { success: false, error: error.message || 'Erreur lors de la suppression' };
    }

    if (data?.error) {
      return { success: false, error: data.error };
    }

    return { success: true, data };
  } catch (error) {
    console.error('Admin delete user error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Une erreur inattendue s\'est produite',
    };
  }
};

// Get user's plan from their most recent shooting
export const getUserPlan = async (userId: string) => {
  try {
    const { data, error } = await supabase
      .from('shootings')
      .select('plan')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error) throw error;
    return { success: true, data: data?.plan || null };
  } catch (error) {
    console.error("Error fetching user plan:", error);
    return { success: false, error };
  }
};

export interface UserWithMetricool {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  metricool_blog_id: string | null;
}

/**
 * Get user with Metricool data by ID
 */
export const getUserWithMetricool = async (userId: string): Promise<{
  success: boolean;
  data?: UserWithMetricool | null;
  error?: string;
}> => {
  try {
    const { data, error } = await supabase
      .from('users')
      .select('id, email, first_name, last_name, metricool_blog_id')
      .eq('id', userId)
      .single();

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('Error fetching user with metricool:', error);
    return { success: false, error: error instanceof Error ? error.message : String(error) };
  }
};

/**
 * Search users with Metricool blog ID
 */
export const searchUsersWithMetricool = async (query: string): Promise<{
  success: boolean;
  data?: UserWithMetricool[];
  error?: string;
}> => {
  if (query.length < 2) {
    return { success: true, data: [] };
  }

  try {
    const { data, error } = await supabase
      .from('users')
      .select('id, email, first_name, last_name, metricool_blog_id')
      .eq('is_active', true)
      .not('metricool_blog_id', 'is', null)
      .or(`email.ilike.%${query}%,first_name.ilike.%${query}%,last_name.ilike.%${query}%`)
      .limit(10);

    if (error) throw error;
    return { success: true, data: data || [] };
  } catch (error) {
    console.error('Error searching users:', error);
    return { success: false, error: error instanceof Error ? error.message : String(error) };
  }
};

/**
 * Get all active users with Metricool blog ID
 */
export const getActiveUsersWithMetricool = async (): Promise<{
  success: boolean;
  data?: Array<{ id: string }>;
  error?: string;
}> => {
  try {
    const { data, error } = await supabase
      .from('users')
      .select('id')
      .not('metricool_blog_id', 'is', null)
      .eq('is_active', true);

    if (error) throw error;
    return { success: true, data: data || [] };
  } catch (error) {
    console.error('Error fetching active users with metricool:', error);
    return { success: false, error: error instanceof Error ? error.message : String(error) };
  }
};

/**
 * Get users without Metricool blog_id
 */
export const getUsersWithoutMetricool = async (): Promise<{
  success: boolean;
  data?: UserWithMetricool[];
  error?: string;
}> => {
  try {
    const { data, error } = await supabase
      .from('users')
      .select('id, email, first_name, last_name, metricool_blog_id')
      .is('metricool_blog_id', null)
      .eq('is_active', true)
      .order('last_name', { ascending: true });

    if (error) {
      return { success: false, error: error.message };
    }

    return { success: true, data: data || [] };
  } catch (error) {
    console.error('Error fetching users without Metricool:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
};

/**
 * Get user basic info (id, email, first_name, last_name, avatar_url)
 */
export const getUserBasicInfo = async (userId: string): Promise<{
  success: boolean;
  data?: {
    id: string;
    email: string;
    first_name: string | null;
    last_name: string | null;
    avatar_url: string | null;
  } | null;
  error?: string;
}> => {
  try {
    const { data, error } = await supabase
      .from('users')
      .select('id, email, first_name, last_name, avatar_url')
      .eq('id', userId)
      .single();

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('Error fetching user basic info:', error);
    return { success: false, error: error instanceof Error ? error.message : String(error) };
  }
};

export interface CreateMetricoolBrandResult {
  success: boolean;
  blog_id?: string;
  user_id?: string;
  brand_name?: string;
  error?: string;
}

/**
 * Create a Metricool brand for a user
 */
export const createMetricoolBrand = async (
  userId: string,
  brandName?: string
): Promise<{
  success: boolean;
  data?: CreateMetricoolBrandResult;
  error?: string;
}> => {
  try {
    const { data, error } = await supabase.functions.invoke('create-metricool-brand', {
      body: {
        user_id: userId,
        brand_name: brandName || undefined,
      },
    });

    if (error) {
      return { success: false, error: error.message };
    }

    if (data?.success) {
      return { success: true, data };
    } else {
      return { success: false, error: data?.error || 'Unknown error' };
    }
  } catch (error) {
    console.error('Error creating Metricool brand:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : String(error),
    };
  }
};
